import {
  useObserver,
} from 'mobx-react';
import React from 'react';

import GeneralFormView from 'components/GeneralFormView';
import {
  stores,
  RequestStatus,
} from 'mobx-store';
import {
  Condition, ConditionStatement, ConditionExpression, ConditionalStatementTypes,
} from 'components/GeneralFormView/Utils/Condition';
import {
  getIn,
} from 'formik';
import * as yup from 'yup';
import {
  SurveyFormViewModel,
} from './surveyFormViewModel';
import {
  SurveyDTO,
} from './surveyDTO';
import GeneralFormFields, {
  FormFieldActions,
} from '../../components/GeneralFormView/types';
import {
  FormFieldsBuilder,
} from '../../components/GeneralFormView/Utils/FormFieldsBuilder';
import { QuestionPreviewType } from 'mobx-store/storeInterfaces/surveyInterface';

const AddEditSurveyScreen: React.FC = () => {
  const optionsForm = new FormFieldsBuilder<SurveyFormViewModel, SurveyFormViewModel['questions'][number]['options']>()
    .addField('text', {
      title: 'option title',
      type: GeneralFormFields.INPUT_FIELD,
      hasTranslations: true,
      initialValue: '',
      fieldOptions: {
      },
    });

  const questionsForm = new FormFieldsBuilder<SurveyFormViewModel, SurveyFormViewModel['questions'][number]>()
    .addField('title', {
      title: 'question title',
      type: GeneralFormFields.INPUT_FIELD,
      hasTranslations: true,
      initialValue: '',
      fieldOptions: {
      },
    })
    .addField('description', {
      title: 'question description',
      type: GeneralFormFields.INPUT_FIELD,
      validationSchema: yup.string().optional(),
      hasTranslations: true,
      initialValue: '',
      fieldOptions: {
      },
    })
    .addField('isMandatory', {
      hasTranslations: false,
      title: 'Mandatory',
      type: GeneralFormFields.TOGGLE_FIELD,
      fieldOptions: {
      },
    })
    .addField('isNotApplicable', {
      hasTranslations: false,
      title: 'Allow Not Applicable',
      type: GeneralFormFields.TOGGLE_FIELD,
      fieldOptions: {
      },
      condition: (values, actions, location) => new Condition({
        values,
        initialStatement: new ConditionStatement({
          action: actions[FormFieldActions.HIDE],
          type: ConditionalStatementTypes.IF,
          expression: new ConditionExpression({
            values,
            expression: (val) => {
              const value = getIn(val, location.split('.').slice(0, -1).join('.')).type;
              if (value[0] === 'TEXT') {
                return true;
              }
              return false;
            },
          }),
        }),
      }).addElse(new ConditionStatement({
        action: actions[FormFieldActions.ENABLE],
        type: ConditionalStatementTypes.ELSE,
        expression: new ConditionExpression({
          values,
          expression: (val) => true,
        }),
      })),
    })
    .addField('isOther', {
      hasTranslations: false,
      title: 'Other',
      type: GeneralFormFields.TOGGLE_FIELD,
      fieldOptions: {
      },
      condition: (values, actions, location) => new Condition({
        values,
        initialStatement: new ConditionStatement({
          action: actions[FormFieldActions.HIDE],
          type: ConditionalStatementTypes.IF,
          expression: new ConditionExpression({
            values,
            expression: (val) => {
              const value = getIn(val, location.split('.').slice(0, -1).join('.')).type;
              if (value[0] === 'TEXT') {
                return true;
              }
              return false;
            },
          }),
        }),
      }).addElse(new ConditionStatement({
        action: actions[FormFieldActions.ENABLE],
        type: ConditionalStatementTypes.ELSE,
        expression: new ConditionExpression({
          values,
          expression: (val) => true,
        }),
      })),
    })
    .addField('type', {
      title: 'type',
      type: GeneralFormFields.SELECT_FIELD,
      hasTranslations: true,
      initialValue: '',
      fieldOptions: {
        isMultiple: false,
        items: [
          {
            title: 'MCQ',
            value: 'MCQ',
          },
          {
            title: 'TEXT',
            value: 'TEXT',
          },
          {
            title: 'RATING',
            value: 'RATING',
          },
        ],
      },
    })
    .addField('questionTextLength', {
      title: 'Questions Text Length',
      type: GeneralFormFields.SELECT_FIELD,
      hasTranslations: true,
      initialValue: [1],
      validationSchema: yup.array(yup.number().required()).min(0),
      fieldOptions: {
        isMultiple: false,
        items: new Array(10).fill(0).map((value, i) => ({
          title: `${i + 1}`,
          value: i + 1,
        })),
      },
      condition: (values, actions, location) => new Condition({
        values,
        initialStatement: new ConditionStatement({
          action: actions[FormFieldActions.HIDE],
          type: ConditionalStatementTypes.IF,
          expression: new ConditionExpression({
            values,
            expression: (val) => {
              const value = getIn(val, location.split('.').slice(0, -1).join('.')).type;
              if (['RATING', 'MCQ'].includes(value[0]) || !value[0]) {
                return true;
              }
              return false;
            },
          }),
        }),
      }).addElse(new ConditionStatement({
        action: actions[FormFieldActions.ENABLE],
        type: ConditionalStatementTypes.ELSE,
        expression: new ConditionExpression({
          values,
          expression: (val) => true,
        }),
      })),
    })
    .addField('mcqPreviewType', {
      title: 'preview type',
      type: GeneralFormFields.SELECT_FIELD,
      hasTranslations: true,
      initialValue: [QuestionPreviewType.SINGLE_SELECT],
      validationSchema: yup.array(yup.string().required()).min(0),
      fieldOptions: {
        isMultiple: false,
        items: [
          {
            title: QuestionPreviewType.SINGLE_SELECT,
            value: QuestionPreviewType.SINGLE_SELECT,
          },
          {
            title: QuestionPreviewType.MULTIPLE_SELECT,
            value: QuestionPreviewType.MULTIPLE_SELECT,
          },
        ],
      },
      condition: (values, actions, location) => new Condition({
        values,
        initialStatement: new ConditionStatement({
          action: actions[FormFieldActions.HIDE],
          type: ConditionalStatementTypes.IF,
          expression: new ConditionExpression({
            values,
            expression: (val) => {
              const value = getIn(val, location.split('.').slice(0, -1).join('.')).type;
              if (['RATING', 'TEXT'].includes(value[0]) || !value[0]) {
                return true;
              }
              return false;
            },
          }),
        }),
      }).addElse(new ConditionStatement({
        action: actions[FormFieldActions.ENABLE],
        type: ConditionalStatementTypes.ELSE,
        expression: new ConditionExpression({
          values,
          expression: (val) => true,
        }),
      })),
    })
    .addField('ratingPreviewType', {
      title: 'preview type',
      type: GeneralFormFields.SELECT_FIELD,
      hasTranslations: true,
      initialValue: [QuestionPreviewType.NUMBERING],
      validationSchema: yup.array(yup.string().required()).min(0),
      fieldOptions: {
        isMultiple: false,
        items: [
          {
            title: QuestionPreviewType.NUMBERING,
            value: QuestionPreviewType.NUMBERING,
          },
          {
            title: QuestionPreviewType.EMOJI,
            value: QuestionPreviewType.EMOJI,
          },
          {
            title: QuestionPreviewType.SLIDER,
            value: QuestionPreviewType.SLIDER,
          },
        ],
      },
      condition: (values, actions, location) => new Condition({
        values,
        initialStatement: new ConditionStatement({
          action: actions[FormFieldActions.HIDE],
          type: ConditionalStatementTypes.IF,
          expression: new ConditionExpression({
            values,
            expression: (val) => {
              const value = getIn(val, location.split('.').slice(0, -1).join('.')).type;
              if (['MCQ', 'TEXT'].includes(value[0]) || !value[0]) {
                return true;
              }
              return false;
            },
          }),
        }),
      }).addElse(new ConditionStatement({
        action: actions[FormFieldActions.ENABLE],
        type: ConditionalStatementTypes.ELSE,
        expression: new ConditionExpression({
          values,
          expression: (val) => true,
        }),
      })),
    })
    .addField('options', {
      title: 'Options',
      type: GeneralFormFields.FIELD_ARRAY,
      hasTranslations: true,
      validationSchema: yup.array(yup.object({
        text: yup.string(),
      })),
      fieldOptions: {
        isCollapsible: false,
        formData: {
          title: '',
          type: GeneralFormFields.NESTED_FORM,
          fieldOptions: {
            formData: optionsForm.formFields,
            isCollapsibleInArray: false,
            isCollapsible: false,
          },
        },
      },
      condition: (values, actions, location) => new Condition({
        values,
        initialStatement: new ConditionStatement({
          action: actions[FormFieldActions.HIDE],
          type: ConditionalStatementTypes.IF,
          expression: new ConditionExpression({
            values,
            expression: (val) => {
              const value = getIn(val, location.split('.').slice(0, -1).join('.')).type;
              if (value[0] === 'TEXT' || value[0] === 'RATING') {
                return true;
              }
              return false;
            },
          }),
        }),
      }).addElse(new ConditionStatement({
        action: actions[FormFieldActions.ENABLE],
        type: ConditionalStatementTypes.ELSE,
        expression: new ConditionExpression({
          values,
          expression: (val) => true,
        }),
      })),
    });
  const {
    formFields,
  } = new FormFieldsBuilder<SurveyFormViewModel>()
    .addField('title', {
      fieldOptions: {
      },
      hasTranslations: true,
      title: 'Title',
      type: GeneralFormFields.INPUT_FIELD,
    })
    .addField('description', {
      fieldOptions: {
      },
      hasTranslations: true,
      title: 'Description',
      type: GeneralFormFields.TEXT_AREA,
    })
    .addField('questions', {
      title: 'Questions',
      type: GeneralFormFields.FIELD_ARRAY,
      hasTranslations: true,
      fieldOptions: {
        isCollapsible: false,
        formData: {
          title: '',
          type: GeneralFormFields.NESTED_FORM,
          validationSchema: yup.array(yup.object({
            title: yup.string().required(),
            isMandatory: yup.boolean().default(false),
            type: yup.array(yup.string().required()).required(),
            options: yup.array(yup.object({
              text: yup.string(),
            })).when('type', {
              is: (type) => type[0] === 'MCQ',
              then: yup.array(yup.object({
                text: yup.string().required(),
              })).required(),
            }),
          })),
          fieldOptions: {
            formData: questionsForm.formFields,
            isCollapsible: false,
            isCollapsibleInArray: true,
            titleColumn: 'title',
          },
        },
      },
    })
    .addField('isActive', {
      hasTranslations: false,
      title: 'Active',
      type: GeneralFormFields.TOGGLE_FIELD,
      fieldOptions: {
      },
    });
  return useObserver(() => (
    <GeneralFormView<SurveyDTO, SurveyFormViewModel>
      defaultLang="ar"
      title="Survey"
      identifier="id"
      otherLanguages={[
        'en'
      ]}
      formData={formFields}
      create={async (formValue) => {
        const res = await stores.surveysStore.createSurvey(formValue, stores.authStore.userData.countries);
        return res;
      }}
      update={async (id, formValue, language) => {
        const res = await stores.surveysStore.updateSurvey(Number(id), formValue, stores.authStore.userData.countries, language);
        return res;
      }}
      get={async (id, language) => {
        await stores.surveysStore.getSurveyById(Number(id), language);
        return new SurveyFormViewModel(stores.surveysStore.selectedSurvey as any);
      }}
      isFetchSuccessful={stores.surveysStore.status === RequestStatus.SUCCESS}
      createOrUpdateStatus={stores.surveysStore.createAndUpdateStatus}
    />
  ));
};

export default AddEditSurveyScreen;
